import React, { ChangeEventHandler } from 'react';
import styles from './Input.scss';
import classNames from 'classnames';
import Textarea from 'react-textarea-autosize';
import { Tooltip, TooltipSkin } from 'wix-ui-tpa/cssVars';
import Check from 'wix-ui-icons-common/on-stage/Check';
import Error from 'wix-ui-icons-common/on-stage/Error';

const textAreaMaxLength = 80;
interface InputProps {
  label: string;
  value: string;
  valid: boolean;
  error?: string;
  onBlur(): any;
  onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  dataHook: string;
  type: string;
  autoFocus: boolean;
  color?: string;
  background: string;
  inputRef?(el): void;
}

const Input: React.FunctionComponent<InputProps> = ({
  label,
  value,
  valid,
  error,
  onBlur,
  onChange,
  dataHook,
  type,
  autoFocus,
  color,
  background,
  inputRef,
}) => {
  const [count, setCount] = React.useState(0);
  const [focus, setFocus] = React.useState(false);

  return (
    <div className={styles.container} data-hook={dataHook}>
      <div
        className={classNames({
          [styles.input]: true,
          [styles.dirty]: !!value,
          [styles.invalid]: !valid,
          [styles.focused]: focus,
        })}
      >
        {type !== 'message' ? (
          <input
            autoComplete="off"
            {...(!valid && !focus && { 'aria-describedby': `${type}-error` })}
            id={type}
            name={type}
            type="text"
            onBlur={() => {
              setFocus(false);
              onBlur();
            }}
            onChange={onChange}
            aria-required={true}
            aria-invalid={!valid}
            autoFocus={autoFocus}
            onFocus={() => setFocus(true)}
            style={{ background, color }}
            ref={inputRef}
          />
        ) : (
          <div>
            {count > 0 && (
              <span className={styles.counter} data-hook={`${type}-counter`}>
                {count}/{textAreaMaxLength}
              </span>
            )}
            <Textarea
              {...(!valid && !focus && { 'aria-describedby': `${type}-error` })}
              id={type}
              name={type}
              onBlur={() => {
                setFocus(false);
                onBlur();
              }}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setCount(e.target.value.length);
                onChange(e);
              }}
              maxRows={5}
              autoFocus={autoFocus}
              maxLength={textAreaMaxLength}
              onFocus={() => setFocus(true)}
              style={{ background, color }}
            />
          </div>
        )}
        <label id={`${type}-label`} htmlFor={type} data-hook="label">
          {label}
        </label>
      </div>
      {!valid ? (
        <div
          id={`${type}-error`}
          data-hook="error"
          className={styles.error}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          aria-describedby={`${type}-error-text`}
          aria-labelledby={`${type}-label`}
          tabIndex={0}
        >
          <span className={styles.srOnly}>{error}</span>
          <Tooltip
            data-hook={`${type}-error-tooltip`}
            appendTo="window"
            skin={TooltipSkin.Error}
            aria-describedby="error-tooltip"
            content={
              <div
                style={{ textAlign: 'center' }}
                data-hook={`${type}-error-text`}
                aria-label={`${type} field error`}
                aria-describedby="error-tooltip"
              >
                {error}
              </div>
            }
            placement="top"
            moveBy={{ y: 4, x: -80 }}
            maxWidth={210}
          >
            <Error style={{ stroke: color }} />
          </Tooltip>
        </div>
      ) : (
        value && (
          <div
            id={`${type}-valid`}
            aria-label={`${type} field valid`}
            tabIndex={0}
            data-hook="valid"
            className={styles.valid}
          >
            <Check style={{ fill: color }} />
          </div>
        )
      )}
    </div>
  );
};

export default Input;
